import {
    CheckCircle,
    Clock,
    Loader2,
    AlertCircle,
    XCircle,
    SkipForward,
    Crown,
    Wifi,
    WifiOff,
    Server,
} from 'lucide-react'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {useI18n} from '@/i18n'
import {
    JobExecutionStatus,
    TaskExecutionStatus,
    ClusterNodeStatus,
    JobSource,
} from '@/types/background-jobs'

/* eslint-disable i18next/no-literal-string -- const definitions */
export const StatusBadgeType = {
    EXECUTION_STATUS: 'execution-status',
    TASK_STATUS: 'task-status',
    SOURCE: 'source',
    NODE_STATUS: 'node-status',
    NODE_ROLE: 'node-role',
} as const
export type StatusBadgeType = typeof StatusBadgeType[keyof typeof StatusBadgeType]
/* eslint-enable i18next/no-literal-string */

interface StatusBadgeProps {
    status: string | null | undefined
    type: StatusBadgeType
    compact?: boolean
}

/* eslint-disable i18next/no-literal-string -- data-testid construction uses template literals */
function buildTestId(type: StatusBadgeType, status: string | null | undefined): string {
    return status ? `badge-${type}-${status}` : `badge-${type}-none`
}
/* eslint-enable i18next/no-literal-string */

export function StatusBadge({status, type, compact = false}: StatusBadgeProps) {
    const i18n = useI18n()
    const testId = buildTestId(type, status)

    if (!status) {
        return <Badge variant="outline" data-testid={testId}>{i18n.jobs.neverRun}</Badge>
    }

    switch (type) {
        case StatusBadgeType.EXECUTION_STATUS:
            return renderExecutionStatus(status, compact, i18n, testId)
        case StatusBadgeType.TASK_STATUS:
            return renderTaskStatus(status, compact, i18n, testId)
        case StatusBadgeType.SOURCE:
            return renderSource(status, i18n, testId)
        case StatusBadgeType.NODE_STATUS:
            return renderNodeStatus(status, i18n, testId)
        case StatusBadgeType.NODE_ROLE:
            return renderNodeRole(status, i18n, testId)
        default:
            return <Badge variant="secondary" data-testid={testId}>{status}</Badge>
    }
}

function renderExecutionStatus(status: string, compact: boolean, i18n: ReturnType<typeof useI18n>, testId: string) {
    switch (status) {
        case JobExecutionStatus.COMPLETED:
            return (
                <Badge variant={BadgeVariant.SUCCESS} className="gap-1" data-testid={testId}>
                    <CheckCircle className="h-3 w-3"/>
                    {!compact && i18n.jobs.completed}
                </Badge>
            )
        case JobExecutionStatus.COMPLETED_WITH_ERRORS:
            return (
                <Badge variant="warning" className="gap-1" data-testid={testId}>
                    <AlertCircle className="h-3 w-3"/>
                    {!compact && i18n.jobs.completedWithErrors}
                </Badge>
            )
        case JobExecutionStatus.RUNNING:
            return (
                <Badge variant="default" className="gap-1" data-testid={testId}>
                    <Loader2 className="h-3 w-3 animate-spin"/>
                    {!compact && i18n.jobs.running}
                </Badge>
            )
        case JobExecutionStatus.CANCELLING:
            return (
                <Badge variant="secondary" className="gap-1" data-testid={testId}>
                    <Loader2 className="h-3 w-3 animate-spin"/>
                    {!compact && i18n.jobs.cancelling}
                </Badge>
            )
        case JobExecutionStatus.FAILED:
            return (
                <Badge variant="destructive" className="gap-1" data-testid={testId}>
                    <AlertCircle className="h-3 w-3"/>
                    {!compact && i18n.jobs.failed}
                </Badge>
            )
        case JobExecutionStatus.CANCELLED:
            return (
                <Badge variant="secondary" className="gap-1" data-testid={testId}>
                    <XCircle className="h-3 w-3"/>
                    {!compact && i18n.jobs.cancelled}
                </Badge>
            )
        case JobExecutionStatus.PENDING:
            return (
                <Badge variant="secondary" className="gap-1" data-testid={testId}>
                    <Clock className="h-3 w-3"/>
                    {!compact && i18n.jobs.pending}
                </Badge>
            )
        default:
            return <Badge variant="secondary" data-testid={testId}>{status}</Badge>
    }
}

function renderTaskStatus(status: string, compact: boolean, i18n: ReturnType<typeof useI18n>, testId: string) {
    switch (status) {
        case TaskExecutionStatus.COMPLETED:
            return (
                <Badge variant={BadgeVariant.SUCCESS} className="gap-1" data-testid={testId}>
                    <CheckCircle className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.completed}
                </Badge>
            )
        case TaskExecutionStatus.COMPLETED_WITH_ERRORS:
            return (
                <Badge variant="warning" className="gap-1" data-testid={testId}>
                    <AlertCircle className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.completedWithErrors}
                </Badge>
            )
        case TaskExecutionStatus.RUNNING:
            return (
                <Badge variant="default" className="gap-1" data-testid={testId}>
                    <Loader2 className="h-3 w-3 animate-spin"/>
                    {!compact && i18n.jobExecutions.running}
                </Badge>
            )
        case TaskExecutionStatus.FAILED:
            return (
                <Badge variant="destructive" className="gap-1" data-testid={testId}>
                    <AlertCircle className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.failed}
                </Badge>
            )
        case TaskExecutionStatus.PENDING:
            return (
                <Badge variant="secondary" className="gap-1" data-testid={testId}>
                    <Clock className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.pending}
                </Badge>
            )
        case TaskExecutionStatus.WAITING:
            return (
                <Badge variant="outline" className="gap-1" data-testid={testId}>
                    <Clock className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.waiting}
                </Badge>
            )
        case TaskExecutionStatus.SKIPPED:
            return (
                <Badge variant="warning" className="gap-1" data-testid={testId}>
                    <SkipForward className="h-3 w-3"/>
                    {!compact && i18n.jobExecutions.skipped}
                </Badge>
            )
        default:
            return <Badge variant="secondary" data-testid={testId}>{status}</Badge>
    }
}

function renderSource(source: string, i18n: ReturnType<typeof useI18n>, testId: string) {
    switch (source) {
        case JobSource.STATIC:
            return <Badge variant="default" data-testid={testId}>{i18n.common.static}</Badge>
        case JobSource.DYNAMIC:
            return <Badge variant="outline" data-testid={testId}>{i18n.common.dynamic}</Badge>
        case JobSource.TESTDATA:
            return <Badge variant="secondary" data-testid={testId}>{i18n.common.testdata}</Badge>
        default:
            return <Badge variant="secondary" data-testid={testId}>{source}</Badge>
    }
}

function renderNodeStatus(status: string, i18n: ReturnType<typeof useI18n>, testId: string) {
    switch (status) {
        case ClusterNodeStatus.ACTIVE:
            return (
                <Badge variant={BadgeVariant.SUCCESS} className="gap-1" data-testid={testId}>
                    <Wifi className="h-3 w-3"/>
                    {i18n.clusterNodes.active}
                </Badge>
            )
        case ClusterNodeStatus.INACTIVE:
            return (
                <Badge variant="warning" className="gap-1" data-testid={testId}>
                    <WifiOff className="h-3 w-3"/>
                    {i18n.clusterNodes.inactive}
                </Badge>
            )
        case ClusterNodeStatus.STARTING:
            return (
                <Badge variant="secondary" className="gap-1" data-testid={testId}>
                    <Loader2 className="h-3 w-3 animate-spin"/>
                    {i18n.clusterNodes.starting}
                </Badge>
            )
        case ClusterNodeStatus.STOPPED:
            return (
                <Badge variant="destructive" className="gap-1" data-testid={testId}>
                    <XCircle className="h-3 w-3"/>
                    {i18n.clusterNodes.stopped}
                </Badge>
            )
        default:
            return <Badge variant="secondary" data-testid={testId}>{status}</Badge>
    }
}

/* eslint-disable i18next/no-literal-string -- const definition */
const NodeRole = {MASTER: 'MASTER'} as const
/* eslint-enable i18next/no-literal-string */

function renderNodeRole(role: string, i18n: ReturnType<typeof useI18n>, testId: string) {
    if (role === NodeRole.MASTER || role === 'true') {
        return (
            <Badge variant="default" className="gap-1" data-testid={testId}>
                <Crown className="h-3 w-3"/>
                {i18n.clusterNodes.master}
            </Badge>
        )
    }
    return (
        <Badge variant="secondary" className="gap-1" data-testid={testId}>
            <Server className="h-3 w-3"/>
            {i18n.clusterNodes.worker}
        </Badge>
    )
}
